import React, { useRef } from 'react';
import { useLingui } from '@lingui/react/macro';

import { SITTER_PROFILE_EVENTSTREAM_EVENTS } from '@rover/constants/src/translations/sitterProfilePage.constants';
import { Box, Heading } from '@rover/kibble/core';
import Divider from '@rover/kibble/patterns/Divider/Divider';
import type { Pet } from '@rover/rsdk/src/apiClient/latest/model/pet';
import { UnitEnum } from '@rover/rsdk/src/apiClient/latest/model/unitEnum';
import useCountryConfigurationShim from '@rover/shared/js/hooks/useCountryConfigurationShim';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

import { SitterProfileVersionOptions } from '../../constants';
import { useFilteredAttributesByPetTypes } from '../../hooks/useFilteredAttributesByPetTypes';
import { useRevampProfileContext } from '../../hooks/useRevampProfileContext';
import PetCompatibilityBanner from '../PetCompatibilityBanner/PetCompatibilityBanner';

import { isHostedServiceType } from './isHostedServiceType';
import RevampHomeCharacteristics from './RevampHomeCharacteristics';
import RevampHomeFeaturesAndHousehold from './RevampHomeFeaturesAndHousehold';
import RevampSafetyTrustSection from './RevampSafetyTrustSection';
import RevampSitterPets from './RevampSitterPets';
import RevampTypicalDay from './RevampTypicalDay';

const RevampWhereYourPetWillStay = (): JSX.Element | null => {
  const { t } = useLingui();
  const { sitterData, serviceType } = useRevampProfileContext();
  const { current: currentCountryConfiguration } = useCountryConfigurationShim();
  const { filterAttributesByPetTypes } = useFilteredAttributesByPetTypes();
  const headingRef = useRef<HTMLElement>(null);

  if (!isHostedServiceType(serviceType)) return null;

  const dogExperience =
    sitterData.providerProfile?.hostingServicePreferences?.dogsExperienceAttributes ?? [];
  const homeAttributes = filterAttributesByPetTypes([
    ...(sitterData.providerProfile?.hostingServicePreferences?.attributes ?? []),
    ...dogExperience.filter((e) => e.key !== 'accepts-only-one-client'),
  ]);

  const weightLabel: UnitEnum =
    currentCountryConfiguration?.weightUnit === UnitEnum.kg ? UnitEnum.kg : UnitEnum.lbs;

  const handleOnPetPressed = (pet: Pet): void => {
    fireDataLayerEvent({
      event: SITTER_PROFILE_EVENTSTREAM_EVENTS.CLICK_ON_SITTER_PET,
      providerId: sitterData.opk,
      pageVariant: SitterProfileVersionOptions.REVAMP,
    });
    if (process.env.JS_ENV_CLIENT) {
      window.open(pet.webUrl, '_blank');
    }
  };

  const hasAnyContent =
    homeAttributes.length > 0 ||
    !!sitterData.environment ||
    (sitterData.pets && sitterData.pets.length > 0) ||
    !!sitterData.providerProfile?.dogsExperience ||
    sitterData.petCompatibility.questions.length > 0;

  if (!hasAnyContent) return null;

  const renderSitterPets = (pt?: '6x'): JSX.Element => (
    <RevampSitterPets
      pets={sitterData.pets ?? []}
      weightLabel={weightLabel}
      onPetPressed={handleOnPetPressed}
      pt={pt}
    />
  );

  return (
    <Box data-testid="revamp-where-your-pet-will-stay" width="100%" pt="6x">
      <Box ref={headingRef} webProps={{ tabIndex: -1 }}>
        <Heading size="400" webProps={{ responsive: false }} fontWeight="semibold" mb="6x">
          {t`Where your pet will stay`}
        </Heading>
      </Box>
      {sitterData.petCompatibility.enabled ? (
        <>
          <RevampHomeFeaturesAndHousehold attributes={homeAttributes} />
          <PetCompatibilityBanner onDismiss={() => headingRef.current?.focus()} />
          {renderSitterPets('6x')}
          <RevampSafetyTrustSection environment={sitterData.environment} />
        </>
      ) : (
        <>
          <RevampHomeCharacteristics attributes={homeAttributes} />
          <RevampSafetyTrustSection environment={sitterData.environment} />
          {renderSitterPets()}
        </>
      )}
      <RevampTypicalDay dogsExperience={sitterData.providerProfile?.dogsExperience} />
      <Divider mt="8x" />
    </Box>
  );
};

export default RevampWhereYourPetWillStay;
